/**
 * Last city / buy-rent intent / property kind for repeat visits (URL remains source of truth when present).
 *
 * First-visit city modal (`CityOnboardingGate`): if `KEY_CITY_ONBOARDING` is unset and there is no
 * `KEY_CITY` and no `?city=` in the URL, the user must pick a city. After that, `KEY_CITY_ONBOARDING`
 * is set so the modal does not repeat. `getHomePathForSavedPreferences()` + `?city=` apply on completion.
 */
const KEY_CITY = "proppins_last_city_slug";
const KEY_CITY_ONBOARDING = "proppins_city_onboarding_done";
const KEY_BUY_RENT = "proppins_last_buy_rent";
const KEY_PROPERTY_KIND = "proppins_last_property_kind";

const DEFAULT_CITY = "hyderabad";

export function getLastCitySlug() {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(KEY_CITY);
    if (!v || typeof v !== "string") return null;
    const t = v.trim().toLowerCase();
    return t || null;
  } catch {
    return null;
  }
}

export function setLastCitySlug(slug) {
  if (typeof window === "undefined" || !slug) return;
  try {
    const s = String(slug).trim().toLowerCase();
    if (s) localStorage.setItem(KEY_CITY, s);
  } catch {
    /* ignore */
  }
}

/** First-visit city picker: once set, we don’t show the full-screen prompt again. */
export function hasCompletedCityOnboarding() {
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(KEY_CITY_ONBOARDING) === "1";
  } catch {
    return true;
  }
}

export function setCityOnboardingDone() {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(KEY_CITY_ONBOARDING, "1");
  } catch {
    /* ignore */
  }
}

/** @returns {"buy"|"rent"|null} */
export function getLastBuyRent() {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(KEY_BUY_RENT);
    if (v === "buy" || v === "rent") return v;
    return null;
  } catch {
    return null;
  }
}

/** @param {"buy"|"rent"} value */
export function setLastBuyRent(value) {
  if (typeof window === "undefined") return;
  if (value !== "buy" && value !== "rent") return;
  try {
    localStorage.setItem(KEY_BUY_RENT, value);
  } catch {
    /* ignore */
  }
}

/** @returns {"apartment"|"villa"|"plot"|null} */
export function getLastPropertyKind() {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(KEY_PROPERTY_KIND);
    if (v === "apartment" || v === "villa" || v === "plot") return v;
    return null;
  } catch {
    return null;
  }
}

/** @param {"apartment"|"villa"|"plot"} value */
export function setLastPropertyKind(value) {
  if (typeof window === "undefined") return;
  if (value !== "apartment" && value !== "villa" && value !== "plot") return;
  try {
    localStorage.setItem(KEY_PROPERTY_KIND, value);
  } catch {
    /* ignore */
  }
}

/** Display label from URL slug (e.g. hyderabad -> Hyderabad, bengaluru-north -> Bengaluru North) */
export function slugToDisplayName(slug) {
  if (!slug || typeof slug !== "string") return DEFAULT_CITY.replace(/^\w/, (c) => c.toUpperCase());
  return slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export { DEFAULT_CITY };
